import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  FileWarning, 
  Calendar, 
  MessageSquare,
  Download,
  Copy,
  Check,
  Loader2,
  AlertTriangle,
  Clock,
  Target,
  Users,
  HelpCircle,
  Lightbulb
} from 'lucide-react'
import { clsx } from 'clsx'
import api from '@/services/api'

interface PostMortemPanelProps {
  dealId: string
  dealName: string
}

// 归因类型配置
const ATTRIBUTION_TYPES = {
  timing: {
    label: '时机因素',
    icon: Clock,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  fit: {
    label: '匹配度因素',
    icon: Target,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  competition: {
    label: '竞争因素',
    icon: Users,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  process: {
    label: '流程因素',
    icon: Lightbulb,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  unknown: {
    label: '未知因素',
    icon: HelpCircle,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
  },
}

export default function PostMortemPanel({ dealId, dealName }: PostMortemPanelProps) {
  const [step, setStep] = useState<'input' | 'result'>('input')
  const [lostDate, setLostDate] = useState('')
  const [lostReason, setLostReason] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [reportContent, setReportContent] = useState<string | null>(null)
  const [attributionType, setAttributionType] = useState<string | null>(null)
  const [isExecutionIssue, setIsExecutionIssue] = useState<boolean | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGenerate = async () => {
    if (!lostDate || !lostReason) {
      setError('请填写丢单日期和客户反馈原因')
      return
    }

    setIsGenerating(true)
    setError(null)

    try {
      const response = await api.post(`/post-mortem/deal/${dealId}/generate`, {
        lost_date: lostDate,
        lost_reason_customer: lostReason,
      })

      if (response.data.markdownContent) {
        setReportContent(response.data.markdownContent)
        setAttributionType(response.data.attributionType)
        setIsExecutionIssue(response.data.isExecutionIssue)
        setStep('result')
      } else if (response.data.error) {
        setError(response.data.error)
      }
    } catch (err: any) {
      setError(err.message || '生成失败，请重试')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopy = async () => {
    if (reportContent) {
      await navigator.clipboard.writeText(reportContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleDownload = () => {
    if (reportContent) {
      const blob = new Blob([reportContent], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${dealName}_复盘报告_${lostDate}.md`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  const attrConfig = attributionType ? ATTRIBUTION_TYPES[attributionType as keyof typeof ATTRIBUTION_TYPES] : null

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* 头部 */}
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-200 rounded-lg">
            <FileWarning className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">输单复盘</h2>
            <p className="text-sm text-gray-500">
              非问责式分析，聚焦系统性学习
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <AnimatePresence mode="wait">
          {/* 输入步骤 */}
          {step === 'input' && (
            <motion.div
              key="input"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* 提示信息 */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">关于复盘报告</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-600">
                      <li>分析"什么假设被证伪"而非"谁做错了"</li>
                      <li>识别"下次可以提前做什么"</li>
                      <li>承认销售过程中存在大量不可控因素</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 表单 */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    丢单日期 *
                  </label>
                  <input
                    type="date"
                    value={lostDate}
                    onChange={(e) => setLostDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <MessageSquare className="w-4 h-4 inline mr-1" />
                    客户反馈的丢单原因 *
                  </label>
                  <textarea
                    value={lostReason}
                    onChange={(e) => setLostReason(e.target.value)}
                    rows={4}
                    placeholder="例如：选择了竞争对手A，因为他们的价格更低 / 项目暂时搁置，预算被冻结..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* 错误提示 */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {error}
                  </div>
                )}

                {/* 生成按钮 */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className={clsx(
                    'w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors',
                    isGenerating
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  )}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      正在生成复盘报告...
                    </>
                  ) : (
                    <>
                      <FileWarning className="w-5 h-5" />
                      生成复盘报告
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* 结果步骤 */}
          {step === 'result' && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* 归因类型卡片 */}
              {attrConfig && (
                <div className={clsx('p-4 rounded-xl mb-4', attrConfig.bgColor)}>
                  <div className="flex items-center gap-3">
                    <attrConfig.icon className={clsx('w-6 h-6', attrConfig.color)} />
                    <div>
                      <div className="font-medium text-gray-900">
                        主要归因: {attrConfig.label}
                      </div>
                      <div className="text-sm text-gray-600">
                        {isExecutionIssue 
                          ? '存在可改进的执行环节'
                          : '主要受外部因素影响'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 操作栏 */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setStep('input')}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  ← 返回修改
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 text-green-500" />
                        <span className="text-green-600">已复制</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        复制
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    下载
                  </button>
                </div>
              </div>

              {/* 报告内容 */}
              <div className="max-h-[60vh] overflow-y-auto p-4 bg-gray-50 rounded-xl border border-gray-200">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
                  {reportContent}
                </pre>
              </div>

              {/* 免责声明 */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2 text-sm text-amber-700">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    本报告旨在系统性学习，分析的是"什么假设被证伪"而非"谁做错了"。
                    销售过程中存在大量不可控因素，报告聚焦于可控环节的优化机会。
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

